import * as _ from 'lodash';

import { OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import { Component, ElementRef, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { BehaviorSubject, Observable, Subject, combineLatest } from 'rxjs';
import { distinctUntilChanged, filter, map, shareReplay, startWith, withLatestFrom } from 'rxjs/operators';

import { FcpBadgeModule, FcpBadgeSize } from '../fcp-badge';
import { FcpIconModule } from '../fcp-icon';
import { FcpTooltipModule, TooltipPosition, TooltipPositionX, TooltipPositionY } from '../fcp-tooltip';

@Component({
    imports: [CommonModule, OverlayModule, FcpIconModule, FcpBadgeModule, FcpTooltipModule],
    selector: 'fcp-badge-list',
    templateUrl: './fcp-badge-list.component.html',
    styleUrls: ['./fcp-badge-list.component.less']
})
export class FcpBadgeListComponent implements OnInit, OnChanges {
    @Input() badges: FcpBadgeElement[];
    @Input() badgeListConfig: FcpBadgeListConfig;

    @ViewChild('containerRef') containerRef: ElementRef;

    public visibleBadges: Observable<FcpBadgeElement[]>;
    public visibleBadgesCount: Observable<number>;
    private visibleBadgesSubject: Subject<void> = new Subject();

    private badgesSubject: Subject<FcpBadgeElement[]> = new BehaviorSubject([]);
    private tooltipSubject: Subject<string[]> = new BehaviorSubject([]);
    public tooltip: Observable<string>;
    public showTooltipSubject: Subject<boolean> = new Subject();
    public tooltipPosition: TooltipPosition = {
        originX: TooltipPositionX.Center,
        originY: TooltipPositionY.Top
    };

    public badgeSizeEnum = FcpBadgeSize;

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.badges && changes.badges.currentValue) {
            this.badgesSubject.next(changes.badges.currentValue);
            if (!changes.badges.firstChange) {
                this.initializeVisibleBadges();
            }
        }
    }

    ngOnInit() {
        this.initializeVisibleBadges();
    }

    getVisibleBadges(): number {
        const containerHeight = this.containerRef.nativeElement.offsetHeight;
        const badgeElements = this.containerRef.nativeElement.querySelectorAll('.badge-list-element .badge');
        let visibleBadgeCount = 0;
        for (let i = 0; i < badgeElements.length; i++) {
            const badgeTop = badgeElements[i].offsetTop;
            if (badgeTop <= containerHeight) {
                visibleBadgeCount++;
            } else {
                break;
            }
        }
        // If badge counter is not visible, remove one more badge
        const badgeCounter = this.containerRef.nativeElement.querySelector('.badge-list-counter .badge');
        if (badgeCounter) {
            if (badgeCounter.offsetTop > containerHeight) {
                visibleBadgeCount--;
            }
        }
        return visibleBadgeCount;
    }

    toggleTooltip(state: boolean) {
        this.showTooltipSubject.next(state);
    }

    initializeVisibleBadges() {
        this.visibleBadgesCount = this.visibleBadgesSubject.pipe(
            map(() => this.getVisibleBadges()),
            shareReplay()
        );

        this.tooltip = combineLatest([this.tooltipSubject, this.visibleBadgesCount]).pipe(
            filter(([badgesText]) => !!badgesText.length),
            map(([badgesText, count]) => badgesText.slice(count, badgesText.length).join(', ')),
            shareReplay()
        );
        this.visibleBadges = this.visibleBadgesCount.pipe(
            startWith(0),
            distinctUntilChanged(),
            withLatestFrom(this.badgesSubject),
            map(([visibleBadgesCount, badges]) => {
                // Trigger calculation everytime visibleBadgesCount changes (edge case where count badge was not visible)
                setTimeout(() => this.visibleBadgesSubject.next());
                // First rendering of badges should display them all, to calculate visible badges
                if (!visibleBadgesCount || visibleBadgesCount === badges.length) {
                    // If all badges are visible, don't show tooltip or collapse
                    this.tooltipSubject.next([]);
                    return badges;
                }
                if (this.badgeListConfig.tooltip.enabled) {
                    this.tooltipSubject.next(_.map(badges, (badge) => badge.text));
                }
                return [
                    ...badges.slice(0, visibleBadgesCount),
                    {
                        text: `+${badges.length - visibleBadgesCount}`,
                        size: this.badgeSizeEnum.SmallRound
                    }
                ];
            })
        );
    }
}

export interface FcpBadgeListConfig {
    width?: string;
    height?: string;
    inversed?: boolean;
    position?: 'start' | 'end';
    tooltip?: {
        enabled?: boolean;
        maxWidth?: string;
    };
}

export interface FcpBadgeElement {
    text: string;
    logo?: string[];
    maxWidth?: string;
    size?: FcpBadgeSize;
}
